import { Guard, Home, RequireAuth, RequireIdentity } from '@/auth/guards';
import Abonnement from '@/pages/Abonnement';
import Ansatt from '@/pages/Ansatt';
import Ansatte from '@/pages/Ansatte';
import Arbeidstid from '@/pages/Arbeidstid';
import Crash from '@/pages/Crash';
import Fravaer from '@/pages/Fravaer';
import Innstillinger from '@/pages/Innstillinger';
import KomIGang from '@/pages/KomIGang';
import Login from '@/pages/Login';
import Lonn from '@/pages/Lonn';
import Min from '@/pages/Min';
import Omsetning from '@/pages/Omsetning';
import Oppsett from '@/pages/Oppsett';
import Placeholder from '@/pages/Placeholder';
import Prosjekter from '@/pages/Prosjekter';
import Timeliste from '@/pages/Timeliste';
import Importer from '@/pages/ansatt/Importer';
import BekreftEpost from '@/pages/konto/BekreftEpost';
import DemoLenke from '@/pages/konto/DemoLenke';
import DemoStart from '@/pages/konto/DemoStart';
import Fortsett from '@/pages/konto/Fortsett';
import GlemtPassord from '@/pages/konto/GlemtPassord';
import Leads from '@/pages/konto/Leads';
import NyttPassord from '@/pages/konto/NyttPassord';
import OpprettBedrift from '@/pages/konto/OpprettBedrift';
import Registrer from '@/pages/konto/Registrer';
import SeSelv from '@/pages/konto/SeSelv';
import Deltid from '@/pages/lonn/Deltid';
import MittFravaer from '@/pages/min/Fravaer';
import Laget from '@/pages/min/Laget';
import Plan from '@/pages/min/Plan';
import Profil from '@/pages/min/Profil';
import Oppgaver from '@/pages/oppgaver/Oppgaver';
import { PlanTabs } from '@/pages/oppgaver/PlanTabs';
import Oversikt from '@/pages/oversikt/Oversikt';
import Vaktplan from '@/pages/vaktplan/Vaktplan';
import Shell from '@/shell/Shell';
import { Navigate, createBrowserRouter } from 'react-router-dom';

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/logg-inn/fortsett', element: <Fortsett /> },
  // a person's own account, before and beside a company (TF-ACCOUNT-ACCESS, 2026-09-15)
  { path: '/registrer', element: <Registrer /> },
  // the email link of the demo with only an address (owner, 2026-09-27)
  { path: '/demo', element: <DemoLenke /> },
  { path: '/se/:code', element: <SeSelv /> }, // «Se selv» in a cold email, plan 0030
  { path: '/bekreft-epost', element: <BekreftEpost /> },
  { path: '/glemt-passord', element: <GlemtPassord /> },
  { path: '/nytt-passord', element: <NyttPassord /> },
  {
    element: <RequireIdentity />,
    children: [
      { path: '/konto/opprett-bedrift', element: <OpprettBedrift /> },
      // a person's private demo, where the site's "Prøv demo" lands (plan 0028)
      { path: '/konto/demo', element: <DemoStart /> },
      // demo leads, the owner's list; the server decides who sees it (LEADS_VIEWERS, 2026-09-26)
      { path: '/leads', element: <Leads /> },
    ],
  },
  {
    element: <RequireAuth />,
    errorElement: <Crash />,
    children: [
      // a new company's first-run setup (plan 0028 slice 3), on the account screens' layout, outside the shell
      { path: 'oppsett', element: <Oppsett /> },
      // price and payment, the administrator's, on the same layout; the server refuses anyone else (plan 0028 slice 4)
      { path: 'abonnement', element: <Abonnement /> },
      {
        element: <Shell />,
        errorElement: <Crash />,
        children: [
          { index: true, element: <Home /> },
          {
            path: 'hjem',
            element: (
              <Guard cap="timeliste.se">
                <Oversikt tab="dagen" />
              </Guard>
            ),
          },
          // the employee's own pages (owner's employee UI brief, 2026-09-22)
          { path: 'min', element: <Min /> },
          { path: 'min/plan', element: <Plan /> },
          {
            path: 'min/laget',
            element: (
              <Guard cap="ansatte.se">
                <Laget />
              </Guard>
            ),
          },
          { path: 'min/fravaer', element: <MittFravaer /> },
          { path: 'min/profil', element: <Profil /> },
          // Vaktplan for laget until 2026-09-22; old links and bookmarks still land
          { path: 'lagvakt', element: <Navigate to="/min/laget" replace /> },
          // Oversikt → Perioden, the month from plan to pay basis (#273 v3.1)
          {
            path: 'perioden',
            element: (
              <Guard cap="timeliste.se">
                <Oversikt tab="perioden" />
              </Guard>
            ),
          },
          {
            path: 'varsler',
            element: (
              <Guard cap="timeliste.se">
                <Oversikt tab="oppfolging" />
              </Guard>
            ),
          },
          // the page was Innboks until 2026-09-21; old links and bookmarks still land
          { path: 'innboks', element: <Navigate to="/varsler" replace /> },
          {
            path: 'vaktplan',
            element: (
              <Guard cap="vaktplan.se">
                <>
                  <PlanTabs />
                  <Vaktplan />
                </>
              </Guard>
            ),
          },
          {
            // Oppgaver (#293): work with one accountable person, beside the planner
            path: 'vaktplan/oppgaver',
            element: (
              <Guard cap="vaktplan.se">
                <>
                  <PlanTabs />
                  <Oppgaver />
                </>
              </Guard>
            ),
          },
          {
            path: 'dagen',
            element: (
              <Guard cap="vaktplan.se">
                <Oversikt tab="dagen" base="/dagen" />
              </Guard>
            ),
          },
          {
            path: 'omsetning',
            element: (
              <Guard cap="vaktplan.kost">
                <Omsetning />
              </Guard>
            ),
          },
          {
            path: 'deltid',
            element: (
              <Guard cap="timerloenn.se">
                <Deltid />
              </Guard>
            ),
          },
          {
            path: 'timeliste',
            element: (
              <Guard cap="timeliste.se">
                <Timeliste />
              </Guard>
            ),
          },
          {
            path: 'prosjekter',
            element: (
              <Guard cap="prosjekter.se">
                <Prosjekter />
              </Guard>
            ),
          },
          { path: 'fravaer', element: <Navigate to="/fravaer/soknader" replace /> },
          {
            path: 'fravaer/:tab',
            element: (
              <Guard cap="fravaer.se">
                <Fravaer />
              </Guard>
            ),
          },
          {
            path: 'ansatte',
            element: (
              <Guard cap="ansatte.se">
                <Ansatte />
              </Guard>
            ),
          },
          {
            path: 'ansatte/importer',
            element: (
              <Guard cap="ansatte.endre">
                <Importer />
              </Guard>
            ),
          },
          {
            path: 'ansatte/:id',
            element: (
              <Guard cap="ansatte.se">
                <Ansatt />
              </Guard>
            ),
          },
          {
            path: 'ansatte/:id/:fane',
            element: (
              <Guard cap="ansatte.se">
                <Ansatt />
              </Guard>
            ),
          },
          {
            path: 'kom-i-gang',
            element: (
              <Guard cap="ansatte.endre">
                <KomIGang />
              </Guard>
            ),
          },
          {
            path: 'lonn',
            element: (
              <Guard cap="timerloenn.se">
                <Lonn />
              </Guard>
            ),
          },
          {
            path: 'arbeidstid',
            element: (
              <Guard cap="timeliste.se">
                <Arbeidstid />
              </Guard>
            ),
          },
          { path: 'innstillinger', element: <Navigate to="/innstillinger/arbeidsmate" replace /> },
          {
            path: 'innstillinger/:tab',
            element: (
              <Guard cap="innstillinger.se">
                <Innstillinger />
              </Guard>
            ),
          },
          { path: '*', element: <Placeholder title="Siden finnes ikke" stage={0} /> },
        ],
      },
    ],
  },
]);
